// Patrones para detectar el tipo de tarjeta (emisor)
const CARD_PATTERNS = [
  { id: 1, title: 'Visa', pattern: /^4/ },
  { id: 104, title: 'Mastercard', pattern: /^(5[1-5]|2[2-7])/ },
  { id: 65, title: 'American Express', pattern: /^3[47]/ },
  { id: 24, title: 'Naranja', pattern: /^589562/ }
];

// Patrones de tarjetas de débito conocidas (no Visa Débito, que requiere tabla de Decidir)
const DEBIT_PATTERNS = [
  /^(50|56|57|58|6[0-9])/, // Maestro / Maestro Debit / Cabal Débito / Otras redes
];

// BINs de Visa débito/prepaga (Fintechs) que la tabla oficial de Decidir
// (_visaDebitRegex) no reconoce y por lo tanto pasarían como crédito.
// Se agregan a mano a medida que se detectan casos puntuales.
const KNOWN_VISA_DEBIT_BINS = [
  /^427836/, // Fintech - Visa débito/prepaga
];

/**
 * Detecta la marca de la tarjeta basándose en el BIN
 * @param {string} cardNumber Número de tarjeta completo o parcial
 * @returns {Object|null} Objeto con id y title de la tarjeta, o null si no se reconoce
 */
export function detectCardBrand(cardNumber) {
  if (!cardNumber) return null;
  const cleanNumber = cardNumber.replace(/\s+/g, '');
  
  if (cleanNumber.length < 1) return null;

  for (const brand of CARD_PATTERNS) {
    if (brand.pattern.test(cleanNumber)) {
      return { id: brand.id, title: brand.title };
    }
  }
  return null;
}

/**
 * Devuelve la máscara de formato adecuada según la marca de la tarjeta.
 * American Express usa 15 dígitos con agrupación 4-6-5; el resto usa 16 dígitos 4-4-4-4.
 * @param {string} cardNumber Número de tarjeta completo o parcial
 * @returns {string} Máscara compatible con vue-the-mask
 */
export function getCardMask(cardNumber) {
  const cleanNumber = String(cardNumber || '').replace(/\D/g, '');
  return /^3[47]/.test(cleanNumber) ? '#### ###### #####' : '#### #### #### ####';
}

/**
 * Valida que la cantidad de dígitos del número de tarjeta sea la correcta según la marca.
 * American Express -> 15 dígitos; el resto -> 16 dígitos.
 * @param {string} cardNumber Número de tarjeta (con o sin espacios)
 * @returns {boolean} true si la longitud es válida para la marca
 */
export function hasValidCardLength(cardNumber) {
  if (!cardNumber) return false;
  const cleanNumber = String(cardNumber).replace(/\D/g, '');
  const isAmex = /^3[47]/.test(cleanNumber);
  return cleanNumber.length === (isAmex ? 15 : 16);
}

/**
 * Detecta si el número de tarjeta pertenece a una red de débito conocida,
 * o si coincide con la expresión regular de Visa Débito extraída de Decidir.
 * @param {string} cardNumber Número de tarjeta completo o parcial
 * @param {RegExp} visaDebitRegex Expresión regular de Decidir para Visa Débito
 * @returns {boolean} true si es débito, false si es crédito o desconocido
 */
export function isDebitCard(cardNumber, visaDebitRegex) {
  if (!cardNumber) return false;
  const cleanNumber = cardNumber.replace(/\s+/g, '');

  if (cleanNumber.length < 6) return false; // Necesitamos al menos 6 dígitos para validar el BIN de forma segura

  // 1. Validar contra patrones genéricos de débito (Maestro, etc)
  // Nota: Filtramos el patrón de Naranja para que no se confunda con Maestro
  if (!cleanNumber.startsWith('589562')) {
    for (const pattern of DEBIT_PATTERNS) {
      if (pattern.test(cleanNumber)) {
        return true;
      }
    }
  }

  // 2. Validar contra BINs de Fintech conocidos que Decidir no cubre
  if (cleanNumber.startsWith('4')) {
    if (KNOWN_VISA_DEBIT_BINS.some(pattern => pattern.test(cleanNumber))) {
      return true;
    }

    // 3. Validar contra la regex oficial de Decidir para Visa Débito (si está disponible)
    if (visaDebitRegex && visaDebitRegex.test(cleanNumber)) {
      return true;
    }
  }

  return false;
}
